import { Clock, Lock, MailWarning } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAuthContext } from "@/lib/useAuthContext";
import { canManageBilling } from "@/lib/roles";
import { useResendVerification } from "@/hooks/useAuth";
import {
  useCreateCheckoutSession,
  useSubscriptionState,
} from "@/hooks/useBilling";

/**
 * Trial + email-verification banner shown at the top of the
 * authenticated app shell.
 *
 * Three surfaces, in priority order:
 *
 * 1. Email-not-verified — offers a re-send action.
 * 2. Trial-active — surfaces ``trial_days_remaining`` so the user is
 *    aware of when paid billing kicks in. The "Manage plan" CTA jumps
 *    to Settings → Billing where the user can pre-commit to a tier
 *    before the trial ends.
 * 3. Trial-ended (verified email + ``trial_active=false`` + the
 *    subscription is NOT paid-and-current) — surfaces an upgrade CTA.
 *    We try Stripe Checkout directly via
 *    ``POST /api/v1/billing/checkout-session`` for "Pro · monthly"
 *    (the most common conversion target); on any error the user falls
 *    back to ``/settings/billing`` where they can pick another plan or
 *    see the failure surface.
 *
 * "Paid and current" reads ``is_active_or_trialing`` from
 * ``GET /api/v1/billing/subscription`` — the backend entitlement
 * predicate (``active`` / ``trialing`` / ``past_due`` inside the
 * dunning grace window / active enterprise contract, per
 * ``backend/app/billing/entitlements.py``). The red trial-ended
 * surface renders only on POSITIVE evidence of a lapsed/unconverted
 * subscription: the read must resolve with an explicit
 * ``is_active_or_trialing: false`` — while it is in flight, errored,
 * or field-less, nothing renders. A paid workspace must never see a
 * flash of "your trial has ended".
 *
 * Pre-trial workspaces (no trial ever started + no subscription row)
 * see NO banner either: the trial-ended copy would be false at the
 * activation moment, and the /onboarding CcTrialGate is the intended
 * pre-trial paywall surface.
 *
 * The banner self-hides when none of the three apply (verified email
 * + paid subscription active).
 */

/**
 * ``POST /auth/resend-verification`` answers 202 for an already-verified
 * user too, with ``detail: "Email already verified; no token sent."``
 * (backend/app/api/v1/auth.py) — the only field that tells the two apart.
 */
const ALREADY_VERIFIED_DETAIL = /already verified/i;

export default function TrialBanner() {
  const { t } = useTranslation();
  const { session, refresh } = useAuthContext();
  const location = useLocation();
  const resend = useResendVerification();
  const checkout = useCreateCheckoutSession();
  const subscription = useSubscriptionState();

  if (!session) return null;
  // Onboarding IS the pre-trial flow: a fresh value-first signup is verified
  // but has never started a trial, so the trial-ended branch below would
  // falsely tell them "your free trial has ended" at the activation moment
  // whose whole premise is value-before-paywall. The CcTrialGate is the
  // intended paywall surface there — suppress this global banner on
  // /onboarding* (ENG-ONBOARDING-FLOW-QA finding F1, PO sign-off 2026-06-29).
  if (location.pathname.startsWith("/onboarding")) return null;
  // Destructure defensively rather than `const { user, workspace } = session`.
  // A truthy-but-incomplete envelope passes the `!session` check above and
  // then throws on `user.email_verified` below — and this banner renders from
  // Layout, so that throw white-screens the whole authenticated shell.
  // AuthProvider's isCompleteSession guard already makes that unreachable;
  // this is the belt to its braces, and unlike a `session?.user?.x` chain it
  // is invisible to the tests/verify chain scanner, so it is spelled out here.
  const user = session.user as typeof session.user | undefined;
  const workspace = session.workspace as typeof session.workspace | undefined;
  if (!user || !workspace) return null;
  // ENG-ROLE-GATE-FOLLOWTHROUGH Item 2 — checkout-session mutation now
  // carries require_role("owner", "billing"); HIDE the CTA (not the
  // banner) for Member/Viewer per the house pattern. The lapsed-workspace
  // notice itself stays visible to everyone (see module comment: invited
  // members are expected to SEE it), only the mutation trigger is gated.
  const canManage = canManageBilling(user.role);

  function handleUpgrade(): void {
    // The "Upgrade now" trail is opinionated by design: drop the user
    // straight into Pro Checkout (the highest-value tier we self-serve).
    // If Stripe is unconfigured the backend returns 503; we fall back
    // to the in-app /settings/billing page where the user can see the
    // unconfigured-state message.
    checkout.mutate(
      { tier: "pro", cadence: "monthly", quantity: 1 },
      {
        onSuccess: ({ url }) => {
          window.location.href = url;
        },
        onError: () => {
          toast.error(t("auth.trialBanner.checkoutStartErrorToast"));
          window.location.href = "/settings/billing";
        },
      },
    );
  }

  function handleResend(): void {
    resend.mutate(undefined, {
      onSuccess: (data) => {
        // 202 is also the answer for an already-verified user (the cached
        // /auth/me was stale): say so, and refetch so the banner clears.
        if (ALREADY_VERIFIED_DETAIL.test(data.detail)) {
          toast.success(t("auth.trialBanner.alreadyVerifiedToast"));
          void refresh();
          return;
        }
        toast.success(t("auth.trialBanner.verificationResentToast"));
      },
      onError: () => {
        // Every failure toasts OUR translated copy, never backend or axios
        // text (review 2026-10-17: the status allowlist let a 403 / 429 / 422
        // body through).
        toast.error(t("auth.trialBanner.resendFailedToast"));
      },
    });
  }

  if (!user.email_verified) {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900",
        )}
      >
        <span className="flex items-center gap-2">
          <MailWarning size={16} aria-hidden="true" />
          {t("auth.trialBanner.confirmYourEmail")}
        </span>
        <button
          type="button"
          disabled={resend.isPending}
          onClick={handleResend}
          className={cn(
            "rounded-md border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-900",
            "hover:bg-amber-100",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
            resend.isPending && "opacity-60",
          )}
        >
          {resend.isPending
            ? t("auth.trialBanner.sendingEllipsis")
            : t("auth.trialBanner.resendEmail")}
        </button>
      </div>
    );
  }

  if (workspace.trial_active) {
    const days = workspace.trial_days_remaining;
    const daysLabel = t("auth.trialBanner.daysLabel", { count: days });
    return (
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-3 border-b border-primary-100 bg-primary-50 px-4 py-2 text-sm text-primary-900",
        )}
      >
        <span className="flex items-center gap-2">
          <Clock size={16} aria-hidden="true" />
          {t("auth.trialBanner.trialEndsIn")}{" "}
          <span className="tabular-nums">{daysLabel}</span>.
        </span>
        <Link
          to="/settings/billing"
          className={cn(
            "rounded-md border border-primary-200 px-3 py-1 text-xs font-semibold text-primary-900",
            "hover:bg-primary-100",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
          )}
        >
          {t("auth.trialBanner.managePlan")}
        </Link>
      </div>
    );
  }

  // Trial over — split paid-and-current from lapsed/unconverted. A paid
  // workspace (active / in-grace past_due / enterprise contract →
  // is_active_or_trialing) gets NO banner. The red surface below
  // requires POSITIVE evidence of a lapse: the subscription read must
  // have resolved with an explicit ``is_active_or_trialing: false`` —
  // loading, errored, or field-less reads hide the banner rather than
  // accuse a paying customer on unresolved evidence.
  const sub = subscription.data;
  if (!sub || sub.is_active_or_trialing !== false) return null;

  // Pre-trial workspace: no trial window was ever started AND no
  // subscription row exists (status "none" is the backend's documented
  // no-row pre-Checkout state). "Your free trial has ended" would be
  // false here — the /onboarding CcTrialGate is the intended paywall
  // surface (same product call as the F1 /onboarding suppression).
  // Invited members joining a genuinely-lapsed workspace still see the
  // banner: the workspace-level trial_end_at is set for them.
  if (workspace.trial_end_at === null && sub.status === "none") return null;

  // Trial ended without conversion (including invited members joining
  // post-trial) or the subscription lapsed. Surface an upgrade CTA so
  // the user always has a path to billing — never strand them with no
  // way to reactivate.
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-900",
      )}
    >
      <span className="flex items-center gap-2">
        <Lock size={16} aria-hidden="true" />
        {t("auth.trialBanner.trialEndedUpgrade")}
      </span>
      {canManage && (
        <button
          type="button"
          disabled={checkout.isPending}
          onClick={handleUpgrade}
          className={cn(
            "rounded-md border border-rose-300 bg-white px-3 py-1 text-xs font-semibold text-rose-900",
            "hover:bg-rose-100",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500",
            checkout.isPending && "opacity-60",
          )}
        >
          {checkout.isPending
            ? t("auth.trialBanner.openingCheckoutEllipsis")
            : t("auth.trialBanner.upgradeNow")}
        </button>
      )}
    </div>
  );
}
